import React, { useState, useCallback } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import indianNumberFormatter from "../../../../utils/indianNumberFormatter";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div
      style={{
        background: "rgba(255, 255, 255, 0.96)",
        backdropFilter: "blur(12px)",
        border: "1px solid #e8e8e8",
        borderRadius: "10px",
        padding: "14px 18px",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
        minWidth: "180px",
      }}
    >
      <p
        style={{
          margin: "0 0 10px 0",
          fontSize: "12px",
          fontWeight: 600,
          color: "#6b7280",
          letterSpacing: "0.3px",
          textTransform: "uppercase",
          borderBottom: "1px solid #f0f0f0",
          paddingBottom: "8px",
        }}
      >
        {label}
      </p>
      {payload
        .filter((entry) => !entry.hide)
        .map((entry, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              padding: "3px 0",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor: entry.name
                    .toLowerCase()
                    .includes("conversion")
                    ? "transparent"
                    : entry.color,
                  border: entry.name.toLowerCase().includes("conversion")
                    ? `2px solid ${entry.color}`
                    : "none",
                  boxSizing: "border-box",
                  flexShrink: 0,
                }}
              />
              <span
                style={{ fontSize: "12px", color: "#374151", fontWeight: 500 }}
              >
                {entry.name}
              </span>
            </div>
            <span
              style={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}
            >
              {indianNumberFormatter(entry.value)}
            </span>
          </div>
        ))}
    </div>
  );
};

const RechartsLineChart = ({
  data = [],
  lines = [],
  height = 420,
  yAxisLeft = {},
  yAxisRight = {},
  yAxisRight2,
  hiddenSeries = [],
}) => {
  const [disabledLines, setDisabledLines] = useState(() => {
    const initial = {};
    hiddenSeries.forEach((name) => {
      initial[name] = true;
    });
    return initial;
  });

  const [hoveredAxis, setHoveredAxis] = useState(null);
  const [hoveredLegend, setHoveredLegend] = useState(null);

  const handleLegendClick = useCallback((entry) => {
    setDisabledLines((prev) => ({
      ...prev,
      [entry.value]: !prev[entry.value],
    }));
  }, []);

  const renderLegend = useCallback(() => {
    // Map over the provided lines array to strictly enforce order rather than Recharts payload sorting
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: "6px",
          paddingTop: "16px",
        }}
      >
        {lines.map((lineDef, index) => {
          const entryValue = lineDef.name;
          const entryColor = lineDef.color;
          const isDisabled = disabledLines[entryValue];
          return (
            <div
              key={index}
              onClick={() => handleLegendClick({ value: entryValue })}
              onMouseEnter={() => setHoveredLegend(entryValue)}
              onMouseLeave={() => setHoveredLegend(null)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
                padding: "5px 12px",
                borderRadius: "20px",
                background: isDisabled ? "#f5f5f5" : `${entryColor}0D`,
                border: `1px solid ${isDisabled ? "#e0e0e0" : entryColor}30`,
                transition: "all 0.2s ease",
                opacity: isDisabled ? 0.45 : 1,
              }}
            >
              <div
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor: isDisabled
                    ? "#bbb"
                    : entryValue.toLowerCase().includes("conversion")
                      ? "transparent"
                      : entryColor,
                  border: entryValue.toLowerCase().includes("conversion")
                    ? `2px solid ${isDisabled ? "#bbb" : entryColor}`
                    : "none",
                  boxSizing: "border-box",
                  transition:
                    "background-color 0.2s ease, border-color 0.2s ease",
                }}
              />
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: isDisabled ? "#999" : "#374151",
                  userSelect: "none",
                  letterSpacing: "0.2px",
                }}
              >
                {entryValue}
              </span>
            </div>
          );
        })}
      </div>
    );
  }, [disabledLines, handleLegendClick, lines]);

  const containerRef = React.useRef(null);

  const handleMouseMove = useCallback(
    (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const width = rect.width;

      if (x < 80) {
        setHoveredAxis("left");
      } else if (yAxisRight2 && width && x > width - 140 && x <= width - 70) {
        setHoveredAxis("right");
      } else if (yAxisRight2 && width && x > width - 70) {
        setHoveredAxis("right2");
      } else if (!yAxisRight2 && width && x > width - 80) {
        setHoveredAxis("right");
      } else {
        setHoveredAxis(null);
      }
    },
    [yAxisRight2],
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredAxis(null);
  }, []);

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ width: "100%", height }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 8, right: 16, left: 8, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#f0f0f0"
            vertical={false}
          />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: "#9ca3af", fontWeight: 500 }}
            tickLine={false}
            axisLine={{ stroke: "#e5e7eb" }}
            dy={8}
            interval="preserveStartEnd"
          />

          {/* Left Y-Axis */}
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 11, fill: "#9ca3af", fontWeight: 500 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => indianNumberFormatter(Math.round(value))}
            label={
              yAxisLeft.title
                ? {
                    value: yAxisLeft.title,
                    angle: -90,
                    position: "insideLeft",
                    offset: 0,
                    style: {
                      fontSize: 12,
                      fill: yAxisLeft.color || "#6b7280",
                      fontWeight: 600,
                    },
                  }
                : undefined
            }
          />

          {/* Right Y-Axis */}
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 11, fill: "#9ca3af", fontWeight: 500 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => indianNumberFormatter(Math.round(value))}
            label={
              yAxisRight.title
                ? {
                    value: yAxisRight.title,
                    angle: 90,
                    position: "insideRight",
                    offset: 0,
                    style: {
                      fontSize: 12,
                      fill: yAxisRight.color || "#6b7280",
                      fontWeight: 600,
                    },
                  }
                : undefined
            }
          />

          {/* Right Y-Axis 2 */}
          {yAxisRight2 && (
            <YAxis
              yAxisId="right2"
              orientation="right"
              tick={{ fontSize: 11, fill: "#9ca3af", fontWeight: 500 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) =>
                indianNumberFormatter(Math.round(value))
              }
              label={
                yAxisRight2.title
                  ? {
                      value: yAxisRight2.title,
                      angle: 90,
                      position: "insideRight",
                      offset: 0,
                      style: {
                        fontSize: 12,
                        fill: yAxisRight2.color || "#6b7280",
                        fontWeight: 600,
                      },
                    }
                  : undefined
              }
            />
          )}

          <Tooltip content={<CustomTooltip />} />
          <Legend content={renderLegend} />

          {lines.map((line) => {
            const lineAxisId = line.yAxisId || "left";

            let isFaded = false;
            if (hoveredLegend) {
              isFaded = hoveredLegend !== line.name;
            } else if (hoveredAxis) {
              isFaded = hoveredAxis !== lineAxisId;
            }

            return (
              <Line
                key={line.dataKey}
                yAxisId={lineAxisId}
                type="linear"
                dataKey={line.dataKey}
                name={line.name}
                stroke={line.color}
                strokeOpacity={isFaded ? 0.15 : 1}
                strokeWidth={isFaded ? 1.5 : 2.5}
                strokeDasharray={line.dashed ? "5 5" : undefined}
                dot={false}
                activeDot={{
                  r: 5,
                  strokeWidth: 2,
                  stroke: "#fff",
                  fill: line.color,
                }}
                hide={disabledLines[line.name] || false}
                connectNulls
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default React.memo(RechartsLineChart);
